import React, { useState, useMemo } from 'react';
import { BACKEND_URL } from '../utils/backendUrl';
import { getAuthHeader } from '../utils/auth';

// Curated aesthetic image sets mapped to party categories & zones
const AESTHETIC_SETS = {
  kids_whimsical: {
    label: 'Playful & Whimsical',
    focal_table: {
      image: 'https://images.unsplash.com/photo-1530103043960-ef38714abb15?auto=format&fit=crop&w=1400&q=80',
      caption: 'Joyful celebration focal table with colorful balloon accents and dessert pedestals',
      hotspots: [
        { id: 'k1', x: 50, y: 50, name: 'Custom Character / Theme Tiered Cake', estRent: '$35/stand', estBuy: '$95', tip: 'Elevate on an acrylic riser for prominent photo visibility.' },
        { id: 'k2', x: 25, y: 35, name: 'Pastel Confetti Balloon Cloud', estRent: '$45 DIY', estBuy: '$120', tip: 'Mix 5-inch and 12-inch balloons for playful organic volume.' },
        { id: 'k3', x: 75, y: 65, name: 'Favor Boxes & Sweet Treats Bar', estRent: 'DIY', estBuy: '$40/dozen', tip: 'Pre-portion in branded treat boxes so kids can grab and go.' }
      ]
    },
    photo_wall: {
      image: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?auto=format&fit=crop&w=1400&q=80',
      caption: 'Vibrant celebratory backdrop designed for candid group & family portraits',
      hotspots: [
        { id: 'kp1', x: 50, y: 25, name: 'Custom Birthday / Baby Acrylic Cutout', estRent: '$25/rental', estBuy: '$65', tip: 'Hang at 5.5 feet so it frames children and adults standing.' },
        { id: 'kp2', x: 20, y: 55, name: 'Organic Balloon Garland Frame', estRent: '$60/kit', estBuy: '$180', tip: 'Anchor with heavy floor weights behind the backdrop panels.' }
      ]
    },
    tablescape: {
      image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1400&q=80',
      caption: 'Playful dining setup with coordinated plates, colorful party hats, and festive confetti',
      hotspots: [
        { id: 'kt1', x: 50, y: 60, name: 'Shatterproof Pastel Dinnerware', estRent: '$1.50/ea', estBuy: '$3/set', tip: 'Use durable melamie or compostable bamboo for stress-free cleanup.' },
        { id: 'kt2', x: 50, y: 35, name: 'Low-Height Interactive Centerpiece', estRent: '$15/table', estBuy: '$35', tip: 'Add coloring placemats or party trivia cards at each seat.' }
      ]
    },
    beverage_bar: {
      image: 'https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=1400&q=80',
      caption: 'Kid-friendly refreshment hydration station with fruit infused waters and juice dispensers',
      hotspots: [
        { id: 'kb1', x: 45, y: 45, name: 'Double Glass Beverage Dispensers', estRent: '$18/set', estBuy: '$55', tip: 'Fill one with strawberry punch and one with lemon-mint ice water.' },
        { id: 'kb2', x: 75, y: 65, name: 'Striped Paper Straws & Milk Bottles', estRent: '$12/pack', estBuy: '$24', tip: 'Tie custom name tags with colorful baker twine to avoid cup mix-ups.' }
      ]
    }
  },
  boho_garden: {
    label: 'Botanical, Garden & Fairytale',
    focal_table: {
      image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1400&q=80',
      caption: 'Romantic floral-draped focal table with raw linen runner and taper candles',
      hotspots: [
        { id: 'b1', x: 50, y: 48, name: 'Botanical Pressed-Floral Cake', estRent: '$28 stand', estBuy: '$110', tip: 'Pair with raw natural wood cake risers and brass servers.' },
        { id: 'b2', x: 25, y: 30, name: 'Cascading Eucalyptus & Garden Roses', estRent: '$45/m garland', estBuy: '$120', tip: 'Drape diagonally across the front edge down to the floor.' },
        { id: 'b3', x: 78, y: 62, name: 'Antique Brass Candlesticks', estRent: '$3.50/ea', estBuy: '$18/ea', tip: 'Use dripless beeswax or LED tapers for warm flicker.' }
      ]
    },
    photo_wall: {
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1400&q=80',
      caption: 'Circular floral arch with dried pampas grass and gentle natural backlighting',
      hotspots: [
        { id: 'bp1', x: 50, y: 30, name: 'Round Moon Arch Structure', estRent: '$95/weekend', estBuy: '$320', tip: 'Weave dried bunny tails and seasonal foliage through wire mesh.' },
        { id: 'bp2', x: 48, y: 70, name: 'Woven Bohemian Jute Rug', estRent: '$22/rental', estBuy: '$75', tip: 'Defines the photo stage and covers venue flooring seams.' }
      ]
    },
    tablescape: {
      image: 'https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&w=1400&q=80',
      caption: 'Earthy organic tablescape with raw linen, handmade ceramics, and bud vase runners',
      hotspots: [
        { id: 'bt1', x: 50, y: 65, name: 'Hand-dyed Gauze Linen Runner', estRent: '$8/runner', estBuy: '$24', tip: 'Scrunch loosely down the center for relaxed, lived-in texture.' },
        { id: 'bt2', x: 30, y: 45, name: 'Cluster of Mismatched Bud Vases', estRent: '$2/vase', estBuy: '$6/vase', tip: 'Group in clusters of three with varying stems: ranunculus & olive branch.' }
      ]
    },
    beverage_bar: {
      image: 'https://images.unsplash.com/photo-1534073828943-f801091bb18c?auto=format&fit=crop&w=1400&q=80',
      caption: 'Artisanal spritzer & wine bar styled with fresh herbal sprigs and vintage carafes',
      hotspots: [
        { id: 'bb1', x: 45, y: 40, name: 'Vintage Ribbed Glass Carafes', estRent: '$5/ea', estBuy: '$22', tip: 'Display fresh rosemary, lavender, and dehydrated citrus slices.' }
      ]
    }
  },
  glam_disco_neon: {
    label: 'Disco, Neon & Glamour',
    focal_table: {
      image: 'https://images.unsplash.com/photo-1535141192574-5d4897c13136?auto=format&fit=crop&w=1400&q=80',
      caption: 'Dramatic metallic celebration cake centerpiece with illuminated risers and geometric mirror accents',
      hotspots: [
        { id: 'g1', x: 50, y: 52, name: 'Modern Metallic Tiered Cake', estRent: '$35 stand', estBuy: '$130', tip: 'Direct a narrow pin-spot light onto the cake surface.' },
        { id: 'g2', x: 28, y: 32, name: 'Illuminated Acrylic Display Risers', estRent: '$18/set', estBuy: '$50', tip: 'Elevate surrounding desserts to create architectural height.' }
      ]
    },
    photo_wall: {
      image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1400&q=80',
      caption: 'Glowing custom neon statement wall against reflective shimmer fringe backdrop',
      hotspots: [
        { id: 'gp1', x: 50, y: 35, name: 'Custom Neon Script Sign', estRent: '$65/rental', estBuy: '$180', tip: 'Use warm white or rose gold glow rather than stark daylight blue.' },
        { id: 'gp2', x: 25, y: 60, name: 'Iridescent Shimmer Wall Panels', estRent: '$40/kit', estBuy: '$110', tip: 'Position near an oscillating room fan for gentle reflective motion.' }
      ]
    },
    tablescape: {
      image: 'https://images.unsplash.com/photo-1517814901490-301ec5142c16?auto=format&fit=crop&w=1400&q=80',
      caption: 'Dramatic evening tablescape with smoked crystal glassware and dark velvet linens',
      hotspots: [
        { id: 'gt1', x: 50, y: 60, name: 'Smoked Charcoal Coupe Glassware', estRent: '$2.80/glass', estBuy: '$14/glass', tip: 'Instantly elevates signature toast moments in photography.' }
      ]
    },
    beverage_bar: {
      image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&w=1400&q=80',
      caption: 'Sleek illuminated cocktail station with polished copper bar tools and coupe towers',
      hotspots: [
        { id: 'gb1', x: 45, y: 45, name: 'Tiered Champagne Coupe Tower', estRent: '$45/stand', estBuy: '$140', tip: 'Display with dry ice or edible gold shimmer rim.' }
      ]
    }
  },
  rustic_dinner: {
    label: 'Rustic Dinner, Tuscan & Vineyard',
    focal_table: {
      image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1400&q=80',
      caption: 'Warm banquet dessert setup with weathered wood surfaces and olive branch accents',
      hotspots: [
        { id: 'r1', x: 50, y: 50, name: 'Semi-Naked Rustic Berry Cake', estRent: '$20 board', estBuy: '$85', tip: 'Rest on an olive wood cutting board dusted with powdered sugar.' },
        { id: 'r2', x: 30, y: 65, name: 'Fresh Fig & Rosemary Garnishes', estRent: 'Local', estBuy: '$18', tip: 'Scatter whole figs, walnuts, and eucalyptus sprigs across the table.' }
      ]
    },
    photo_wall: {
      image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1400&q=80',
      caption: 'Weathered cedar arch adorned with olive leaves, warm fairy lights, and wine barrels',
      hotspots: [
        { id: 'rp1', x: 25, y: 65, name: 'Authentic Oak Wine Barrel Pedestals', estRent: '$35/ea', estBuy: '$150/ea', tip: 'Functions as sturdy photo prop stands for floral urns or guest book.' }
      ]
    },
    tablescape: {
      image: 'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&w=1400&q=80',
      caption: 'Communal family-style dining table with terracotta tableware and warm festoon lighting',
      hotspots: [
        { id: 'rt1', x: 50, y: 60, name: 'Handcrafted Terracotta Plates', estRent: '$2.20/ea', estBuy: '$12/ea', tip: 'Contrast with crisp white linen napkins tied with rough twine.' }
      ]
    },
    beverage_bar: {
      image: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=1400&q=80',
      caption: 'Tuscan wine & spritz bar with vintage green glass carafes and wooden crate risers',
      hotspots: [
        { id: 'rb1', x: 50, y: 45, name: 'Artisan Wine Decanter & Carafes', estRent: '$8/set', estBuy: '$35', tip: 'Label with calligraphed parchment tags indicating vintage and region.' }
      ]
    }
  },
  beach_tropical: {
    label: 'Tropical, Coastal & Island Luau',
    focal_table: {
      image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1400&q=80',
      caption: 'Breezy coastal focal display with bamboo risers and vibrant monstera leaves',
      hotspots: [
        { id: 't1', x: 50, y: 50, name: 'Coconut & Passionfruit Feature Cake', estRent: '$25 stand', estBuy: '$95', tip: 'Adorn with fresh edible orchids and split monstera greenery.' }
      ]
    },
    photo_wall: {
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=1400&q=80',
      caption: 'Circular rattan arch styled with palm leaves and sea-glass tones',
      hotspots: [
        { id: 'tp1', x: 50, y: 35, name: 'Rattan Boho Archway', estRent: '$85/rental', estBuy: '$280', tip: 'Perfect against beach, backyard lawn, or patio backgrounds.' }
      ]
    },
    tablescape: {
      image: 'https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?auto=format&fit=crop&w=1400&q=80',
      caption: 'Sun-drenched coastal tablescape with woven seagrass chargers and linen',
      hotspots: [
        { id: 'tt1', x: 50, y: 65, name: 'Woven Seagrass Charger Plates', estRent: '$2/ea', estBuy: '$8/ea', tip: 'Adds tactile beach warmth under clean white porcelain.' }
      ]
    },
    beverage_bar: {
      image: 'https://images.unsplash.com/photo-1536935338788-846bb9981813?auto=format&fit=crop&w=1400&q=80',
      caption: 'Tiki-inspired fresh coconut and tropical rum/mocktail station',
      hotspots: [
        { id: 'tb1', x: 50, y: 45, name: 'Fresh Coconut Station with Paper Umbrellas', estRent: 'DIY', estBuy: '$45 kit', tip: 'Pre-crack coconuts and serve with natural bamboo straws.' }
      ]
    }
  },
  classic_elegant: {
    label: 'Classic & Elegant Gala',
    focal_table: {
      image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1400&q=80',
      caption: 'Grand formal focal table with polished silver, white florals, and clean crystal stemware',
      hotspots: [
        { id: 'c1', x: 50, y: 50, name: 'Multi-Tiered White Fondant Statement Cake', estRent: '$35 stand', estBuy: '$150', tip: 'Frame with symmetrical pedestal urns of white hydrangea.' }
      ]
    },
    photo_wall: {
      image: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?auto=format&fit=crop&w=1400&q=80',
      caption: 'Crisp chiffon drape installation with classical white rose florals',
      hotspots: [
        { id: 'cp1', x: 50, y: 35, name: 'Draped Chiffon Pipe & Drape Frame', estRent: '$75/set', estBuy: '$240', tip: 'Backlight with warm amber uplighting for a flattering glow.' }
      ]
    },
    tablescape: {
      image: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?auto=format&fit=crop&w=1400&q=80',
      caption: 'Formal black-tie dinner setting with pressed white damask linen and polished flatware',
      hotspots: [
        { id: 'ct1', x: 50, y: 60, name: 'Silver Beaded Charger Plates & Fine China', estRent: '$3.50/setting', estBuy: '$22/setting', tip: 'Fold napkins into tailored rectangular pockets holding menu cards.' }
      ]
    },
    beverage_bar: {
      image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=1400&q=80',
      caption: 'Champagne bar with crystal flutes and silver ice urns',
      hotspots: [
        { id: 'cb1', x: 50, y: 45, name: 'Polished Silver Champagne Bucket', estRent: '$15/ea', estBuy: '$65', tip: 'Fill with crystal clear ice cubes and fresh white spray roses.' }
      ]
    }
  }
};

const ZONES_CONFIG = [
  { id: 'focal_table', title: 'Dessert & Cake Focal Table', subtitle: 'The visual centerpiece for photo moments and cake cutting', icon: '🍰' },
  { id: 'photo_wall', title: 'Photo Backdrop & Installation', subtitle: 'The designated stage for guest portraits and memories', icon: '🎈' },
  { id: 'tablescape', title: 'Guest Tablescape & Seating', subtitle: 'Linens, centerpieces, dinnerware, and ambient table lighting', icon: '🍽️' },
  { id: 'beverage_bar', title: 'Beverage Bar & Hydration Station', subtitle: 'Self-serve signature drink styling, glassware, and garnishes', icon: '🍹' }
];

const LIGHTING_MODES = [
  { id: 'daylight', label: '☀️ Natural Daylight', filter: 'brightness(1.02) contrast(1.02) saturate(1.02)', bg: 'from-amber-500/5 to-transparent', advice: 'Ideal for daytime celebrations, outdoor gardens, and airy spaces with abundant window light.' },
  { id: 'golden_hour', label: '🌅 Golden Hour', filter: 'sepia(0.2) contrast(1.06) saturate(1.18)', bg: 'from-amber-500/15 via-orange-500/10 to-transparent', advice: 'Warm amber tones flatter skin tones in portraits and create inviting sunset ambiance.' },
  { id: 'evening_glow', label: '🌙 Candlelit Evening', filter: 'brightness(0.92) contrast(1.16) sepia(0.25) saturate(1.12)', bg: 'from-indigo-950/20 via-purple-900/10 to-stone-900/20', advice: 'Dim overhead venue fixtures; rely on clusters of pillar candles, festoon bulbs, and uplights.' }
];

export default function SpatialMoodboard({ plan, theme, onNavigateToShopping, onShareStory }) {
  const [selectedZone, setSelectedZone] = useState('focal_table');
  const [lightingMode, setLightingMode] = useState('golden_hour');
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiCustomZoneImages, setAiCustomZoneImages] = useState({});
  const [generationError, setGenerationError] = useState(null);

  // Detect matching aesthetic preset based on theme name, description, and event type
  const matchedAestheticKey = useMemo(() => {
    const text = `${theme?.name || ''} ${theme?.description || ''} ${plan?.event_data?.event_type || ''} ${plan?.event_data?.custom_event_type || ''}`.toLowerCase();
    
    if (text.includes('kid') || text.includes('baby') || text.includes('child') || text.includes('toddler') || text.includes('whimsical') || text.includes('safari') || text.includes('dinosaur') || text.includes('superhero') || text.includes('cartoon') || text.includes('circus')) {
      return 'kids_whimsical';
    }
    if (text.includes('disco') || text.includes('neon') || text.includes('80s') || text.includes('90s') || text.includes('cyber') || text.includes('glam') || text.includes('night') || text.includes('retro') || text.includes('party') || text.includes('lounge') || text.includes('rave')) {
      return 'glam_disco_neon';
    }
    if (text.includes('dinner') || text.includes('tuscan') || text.includes('wine') || text.includes('vineyard') || text.includes('rustic') || text.includes('harvest') || text.includes('thanksgiving') || text.includes('autumn') || text.includes('wood') || text.includes('farmhouse')) {
      return 'rustic_dinner';
    }
    if (text.includes('beach') || text.includes('tropical') || text.includes('luau') || text.includes('coastal') || text.includes('island') || text.includes('hawaiian') || text.includes('pool') || text.includes('summer')) {
      return 'beach_tropical';
    }
    if (text.includes('wedding') || text.includes('formal') || text.includes('gala') || text.includes('black tie') || text.includes('anniversary') || text.includes('corporate') || text.includes('classic') || text.includes('champagne')) {
      return 'classic_elegant';
    }
    return 'boho_garden';
  }, [theme, plan]);

  const activeSet = AESTHETIC_SETS[matchedAestheticKey] || AESTHETIC_SETS.boho_garden;
  const currentZoneConfig = ZONES_CONFIG.find((z) => z.id === selectedZone) || ZONES_CONFIG[0];
  const activeZoneData = activeSet[selectedZone] || activeSet.focal_table;

  // Use AI generated custom image if available, else theme hero image if on focal table, else contextual curated photo
  const currentImage = aiCustomZoneImages[selectedZone] || 
    (selectedZone === 'focal_table' && (theme?.ai_image_url || theme?.image_url || theme?.image)) || 
    activeZoneData.image;

  const currentLighting = LIGHTING_MODES.find((m) => m.id === lightingMode) || LIGHTING_MODES[1];
  const palette = theme?.color_palette || plan?.color_palette || [];
  const venue = plan?.event_data?.venue_type || plan?.venue_name || plan?.venue || 'Living Room / Home';

  // Derived tactile material suggestions
  const tactileMaterials = useMemo(() => {
    switch (matchedAestheticKey) {
      case 'kids_whimsical':
        return ['Matte Chalkboard Panels', 'Organic Latex Balloons', 'Compostable Bamboo Plates', 'Patterned Gingham Runner', 'Frosted Acrylic Accents'];
      case 'glam_disco_neon':
        return ['Mirrored Faceted Glass', 'Smoked Charcoal Crystal', 'Heavy Crushed Velvet', 'Polished Brass Barware', 'Reflective Mylar Fringe'];
      case 'rustic_dinner':
        return ['Weathered Reclaimed Oak', 'Raw Terracotta Ceramics', 'Unfinished Belgian Linen', 'Hammered Copper Tins', 'Preserved Olive Leaves'];
      case 'beach_tropical':
        return ['Woven Natural Rattan', 'Bleached Driftwood', 'Split Monstera Foliage', 'Braided Seagrass Chargers', 'Clear Sea-Glass Votives'];
      case 'classic_elegant':
        return ['Damask Pressed Linen', 'Silver Beaded Chargers', 'Heavy Lead-Free Crystal', 'Polished Carrara Marble', 'Sheer Chiffon Drapery'];
      default:
        return ['Raw Washed Linen', 'Hand-thrown Stoneware', 'Antique Brushed Brass', 'Dried Bunny Tails & Pampas', 'Unscented Beeswax Tapers'];
    }
  }, [matchedAestheticKey]);

  // Handle on-demand AI room concept generation using the existing Flux Dev endpoint
  const handleGenerateAIConcept = async () => {
    setIsGeneratingAI(true);
    setGenerationError(null);
    try {
      const authHeader = getAuthHeader();
      const colorDesc = palette.slice(0, 3).map(c => c.name || c.hex).join(', ');
      const prompt = `Photorealistic professional event photography of a luxury ${currentZoneConfig.title} setup for an event with theme "${theme?.name || 'Party'}", featuring color scheme (${colorDesc}). Atmosphere: ${currentLighting.label}. Photorealistic event styling, high-end details, clean cohesive decor, 8k resolution.`;

      const res = await fetch(`${BACKEND_URL}/api/generate-theme-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader ? { Authorization: authHeader } : {})
        },
        body: JSON.stringify({
          plan_id: plan?.plan_id,
          theme: {
            name: `${theme?.name || 'Party'} - ${currentZoneConfig.title}`,
            description: prompt,
            visual_prompt: prompt,
            color_palette: palette
          },
          event_input: plan?.event_data || {}
        })
      });

      if (!res.ok) {
        throw new Error('AI generation service returned an error.');
      }
      const data = await res.json();
      if (data.image_url) {
        setAiCustomZoneImages(prev => ({
          ...prev,
          [selectedZone]: data.image_url
        }));
      } else {
        throw new Error(data.message || 'Could not generate custom image.');
      }
    } catch (err) {
      console.warn('AI room concept generation failed:', err);
      setGenerationError('Custom AI render is momentarily unavailable. Displaying curated editorial setup.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  return (
    <div className="space-y-8 font-sans">
      {/* Editorial Header Banner */}
      <div className="rounded-3xl border border-stone-200/90 bg-gradient-to-b from-[#FAF8F5] to-white p-6 sm:p-8 shadow-xs relative overflow-hidden">
        <div className="max-w-4xl relative z-10">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="px-3 py-1 bg-stone-900 text-stone-50 text-xs font-bold rounded-full uppercase tracking-wider">
              Spatial Decor Studio
            </span>
            <span className="px-3 py-1 bg-white border border-stone-200 text-stone-700 text-xs font-semibold rounded-full flex items-center gap-1.5">
              <span>📍 Venue:</span>
              <strong className="text-stone-900">{venue}</strong>
            </span>
            <span className="px-3 py-1 bg-amber-50 border border-amber-200/80 text-amber-900 text-xs font-semibold rounded-full flex items-center gap-1.5">
              <span>✨ Aesthetic:</span>
              <strong>{activeSet.label}</strong>
            </span>
          </div>

          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight leading-snug">
            Interactive Room Zoning & Spatial Decor Blueprint
          </h2>
          <p className="mt-2 text-sm sm:text-base text-stone-600 leading-relaxed font-sans">
            How your aesthetic — <span className="font-semibold text-stone-900 italic font-serif">"{theme?.name || plan?.theme_name || 'Party Theme'}"</span> — translates into real physical zones, rental pieces, and ambient lighting.
          </p>

          {/* Color & Material Swatch Bar */}
          <div className="mt-5 pt-4 border-t border-stone-200/80 flex flex-col md:flex-row md:items-center justify-between gap-4">
            {palette.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500 mr-1">Palette:</span>
                {palette.map((c, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-lg border border-stone-200 shadow-2xs">
                    <span
                      className="w-3.5 h-3.5 rounded-full border border-black/10 shrink-0"
                      style={{ backgroundColor: c.hex || c.code || '#E5E7EB' }}
                    />
                    <span className="text-xs font-semibold text-stone-800">{c.name || `Color ${idx + 1}`}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Lighting Mode Selector */}
            <div className="flex items-center gap-1.5 bg-stone-100 p-1 rounded-xl shrink-0 self-start md:self-auto">
              {LIGHTING_MODES.map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setLightingMode(mode.id)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                    lightingMode === mode.id
                      ? 'bg-white text-stone-900 shadow-xs font-bold'
                      : 'text-stone-600 hover:text-stone-900'
                  }`}
                  title={mode.advice}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            {onShareStory && (
              <button
                type="button"
                onClick={onShareStory}
                className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 hover:opacity-95 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition active:scale-[0.98] shrink-0"
              >
                <span>📸</span>
                <span>Story Card (9:16)</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Zone Selector Horizontal Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
        {ZONES_CONFIG.map((zone) => {
          const isSelected = zone.id === selectedZone;
          return (
            <button
              key={zone.id}
              type="button"
              onClick={() => {
                setSelectedZone(zone.id);
                setActiveHotspot(null);
                setGenerationError(null);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap shrink-0 ${
                isSelected
                  ? 'bg-stone-900 text-white shadow-md shadow-stone-900/10'
                  : 'bg-white hover:bg-stone-100 text-stone-700 border border-stone-200 shadow-2xs'
              }`}
            >
              <span>{zone.icon}</span>
              <span>{zone.title}</span>
            </button>
          );
        })}
      </div>

      {/* Active Zone Detail Visualizer */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Interactive Spatial Canvas (7 cols) */}
        <div className="lg:col-span-7 bg-white rounded-3xl border border-stone-200/90 overflow-hidden shadow-xs flex flex-col">
          {/* Top Bar with AI Generate Action */}
          <div className="p-3.5 sm:p-4 border-b border-stone-100 flex items-center justify-between gap-3 bg-stone-50/50">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-bold text-stone-900 truncate">{currentZoneConfig.title} Blueprint</span>
            </div>
            <button
              type="button"
              onClick={handleGenerateAIConcept}
              disabled={isGeneratingAI}
              className="text-xs font-bold text-stone-800 bg-white hover:bg-stone-100 border border-stone-200/80 px-3 py-1.5 rounded-xl shadow-2xs transition flex items-center gap-1.5 disabled:opacity-50 shrink-0"
              title="Generate a custom AI Flux render tailored to your exact party theme"
            >
              {isGeneratingAI ? (
                <>
                  <span className="w-3 h-3 border-2 border-stone-600 border-t-transparent rounded-full animate-spin" />
                  <span>Rendering Concept...</span>
                </>
              ) : (
                <>
                  <span>✨ Generate Custom AI Concept</span>
                </>
              )}
            </button>
          </div>

          {/* Canvas Image View with Interactive Hotspot Pins */}
          <div className="relative aspect-[16/10] bg-stone-950 overflow-hidden group">
            <img
              src={currentImage}
              alt={currentZoneConfig.title}
              style={{ filter: currentLighting.filter }}
              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
            />
            {/* Ambient Lighting Gradient Overlay */}
            <div className={`absolute inset-0 bg-gradient-to-t ${currentLighting.bg} pointer-events-none transition-all duration-500`} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent pointer-events-none" />

            {/* Interactive Hotspot Pins Overlay */}
            {activeZoneData.hotspots?.map((spot) => {
              const isActive = activeHotspot?.id === spot.id;
              return (
                <div
                  key={spot.id}
                  style={{ top: `${spot.y}%`, left: `${spot.x}%` }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 z-20"
                >
                  <button
                    type="button"
                    onClick={() => setActiveHotspot(isActive ? null : spot)}
                    className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 focus:outline-none ${
                      isActive
                        ? 'bg-white text-stone-900 shadow-xl scale-125 ring-4 ring-white/40'
                        : 'bg-stone-900/80 backdrop-blur-md text-white hover:bg-white hover:text-stone-900 hover:scale-110 shadow-md ring-2 ring-white/60'
                    }`}
                    title={spot.name}
                  >
                    <span className="text-xs font-bold">{isActive ? '×' : '+'}</span>
                  </button>

                  {/* Hotspot Floating Tooltip Card with Adaptive Placement to Prevent Clipping */}
                  {isActive && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className={`
                        absolute w-64 p-3.5 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-stone-200 text-stone-900 z-30 animate-in fade-in zoom-in-95 duration-200
                        ${spot.y < 50 ? 'top-full mt-3' : 'bottom-full mb-3'}
                        ${spot.x < 35 ? 'left-0' : spot.x > 65 ? 'right-0' : 'left-1/2 -translate-x-1/2'}
                      `}
                    >
                      <div className="flex items-start justify-between gap-1 mb-1">
                        <h5 className="text-xs font-bold text-stone-900 leading-snug">{spot.name}</h5>
                        <button
                          onClick={() => setActiveHotspot(null)}
                          className="text-stone-400 hover:text-stone-700 text-xs font-bold p-0.5"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] font-semibold text-stone-600 mb-1.5">
                        <span className="text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200/60">Rent: {spot.estRent}</span>
                        <span className="text-stone-500">Buy: {spot.estBuy}</span>
                      </div>
                      <p className="text-[11px] text-stone-600 leading-relaxed">{spot.tip}</p>
                      {onNavigateToShopping && (
                        <button
                          onClick={onNavigateToShopping}
                          className="mt-2.5 w-full py-1 text-[11px] font-bold bg-stone-900 hover:bg-stone-800 text-white rounded-lg transition"
                        >
                          Find in Shopping List →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Bottom Caption Overlay */}
            <div className="absolute bottom-4 left-4 right-4 text-white pointer-events-none">
              <span className="px-2.5 py-1 bg-white/20 backdrop-blur-md rounded-lg text-[11px] font-bold uppercase tracking-wider text-white mb-1.5 inline-block">
                {currentZoneConfig.icon} {currentZoneConfig.title}
              </span>
              <p className="text-xs sm:text-sm text-stone-200 font-medium line-clamp-1">{activeZoneData.caption}</p>
            </div>
          </div>

          {/* Interactive Instructions Bar */}
          <div className="p-4 bg-stone-50/70 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
            <span className="flex items-center gap-1.5">
              <span>💡</span> Tap any <strong className="text-stone-800 font-bold">+ pin</strong> on the photo to inspect rental pricing & execution notes.
            </span>
            <span className="font-semibold text-stone-600">{activeZoneData.hotspots?.length || 0} Hotspots mapped</span>
          </div>

          {generationError && (
            <div className="p-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-800 font-medium flex items-center justify-between">
              <span>{generationError}</span>
              <button onClick={() => setGenerationError(null)} className="font-bold text-amber-900">✕</button>
            </div>
          )}
        </div>

        {/* Right Sidebar: Key Pieces, Tactile Textures & Lighting (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Recommended Zone Pieces */}
          <div className="bg-white rounded-3xl border border-stone-200/90 p-6 shadow-xs">
            <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-stone-100">
              <div>
                <h4 className="font-serif text-lg font-bold text-stone-900">Recommended Zone Pieces</h4>
                <p className="text-xs text-stone-500">Essential pieces mapped for {currentZoneConfig.title}</p>
              </div>
              <span className="text-xs font-bold text-stone-500 bg-stone-100 px-2.5 py-1 rounded-lg">
                {activeZoneData.hotspots?.length || 3} Pieces
              </span>
            </div>

            <div className="space-y-2.5">
              {activeZoneData.hotspots?.map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => setActiveHotspot(item)}
                  className={`flex items-center justify-between p-3 rounded-2xl border transition cursor-pointer ${
                    activeHotspot?.id === item.id
                      ? 'bg-stone-900 text-white border-stone-900 shadow-md'
                      : 'bg-stone-50/80 border-stone-200/60 hover:bg-stone-100 text-stone-800'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <span className={`w-2 h-2 rounded-full ${activeHotspot?.id === item.id ? 'bg-amber-400' : 'bg-stone-400'}`} />
                    <span className="text-xs sm:text-sm font-semibold truncate">{item.name}</span>
                  </div>
                  <span className={`text-[11px] font-bold shrink-0 ${activeHotspot?.id === item.id ? 'text-stone-300' : 'text-stone-500'}`}>
                    {item.estRent}
                  </span>
                </div>
              ))}
            </div>

            {onNavigateToShopping && (
              <button
                type="button"
                onClick={onNavigateToShopping}
                className="mt-5 w-full h-11 bg-stone-900 hover:bg-stone-800 text-white text-xs sm:text-sm font-bold rounded-xl flex items-center justify-center gap-2 shadow-sm transition active:scale-[0.98]"
              >
                <span>View Full Sourcing & Shopping List</span>
                <span>→</span>
              </button>
            )}
          </div>

          {/* Tactile Material Texture Palette */}
          <div className="bg-white rounded-3xl border border-stone-200/90 p-5 shadow-xs">
            <h5 className="text-xs font-bold uppercase tracking-wider text-stone-900 mb-2.5 flex items-center gap-1.5">
              <span>🧶 Tactile Material Palette</span>
            </h5>
            <div className="flex flex-wrap gap-2">
              {tactileMaterials.map((mat, i) => (
                <span
                  key={i}
                  className="px-2.5 py-1 text-xs font-medium text-stone-700 bg-stone-100/80 rounded-lg border border-stone-200/60 shadow-2xs"
                >
                  {mat}
                </span>
              ))}
            </div>
          </div>

          {/* Ambient Lighting Guide Card */}
          <div className="rounded-3xl border border-amber-200/80 bg-gradient-to-br from-amber-50/80 to-stone-50 p-5 shadow-2xs">
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0">💡</span>
              <div>
                <h5 className="text-xs font-bold uppercase tracking-wider text-amber-900">
                  {currentLighting.label} Execution
                </h5>
                <p className="mt-1 text-xs text-amber-900/80 leading-relaxed font-medium">
                  {currentLighting.advice}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
